import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import { getBlogs, getSettings } from "../api";
import { ArrowUpRight, Calendar } from "lucide-react";

export default function Blogs() {
  const [blogs, setBlogs] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    getBlogs().then(setBlogs);
    getSettings().then(setSettings);
  }, []);

  const featured = blogs[0];
  const rest = blogs.slice(1);

  return (
    <main>
      <PageHero
        title="Insights"
        subtitle={
          settings.blogs_hero_subtitle ||
          "Field reports, technical guides and industry perspectives from the SSC team."
        }
        num="07"
      />

      <section className="py-20 bg-[#fafafa]">
        <div className="max-w-[1500px] mx-auto px-5 md:px-8">

          {/* FEATURED BLOG */}
          {featured && (
            <Link
              to={`/blogs/${featured.id || 0}`}
              className="group grid lg:grid-cols-2 gap-8 mb-20 items-center bg-white border border-[#ececef] rounded-[36px] overflow-hidden p-4 hover:shadow-2xl transition-all duration-500"
            >

              {/* IMAGE */}
              <div className="aspect-[4/3] rounded-[28px] overflow-hidden">
                <img
                  src={featured.image || featured.img}
                  alt={featured.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>

              {/* CONTENT */}
              <div className="p-4 md:p-10">

                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#ef2435]/10 text-[#ef2435] text-[11px] tracking-[0.25em] uppercase font-semibold mb-6">
                  Featured Article
                </div>

                <div className="flex items-center gap-2 text-xs text-gray-500 uppercase tracking-widest font-mono mb-6">
                  <Calendar size={12} />
                  {featured.published_at || featured.date}
                </div>

                <h2 className="text-4xl md:text-6xl font-black leading-[0.95] mb-6 text-gray-900 group-hover:text-[#ef2435] transition-colors">
                  {(featured.title || "").toUpperCase()}
                </h2>

                <p className="text-gray-600 leading-[2] text-lg mb-8">
                  {featured.excerpt}
                </p>

                <div className="inline-flex items-center gap-2 text-[#ef2435] font-semibold text-sm group-hover:gap-4 transition-all">
                  Read Full Article
                  <ArrowUpRight size={18} />
                </div>

              </div>
            </Link>
          )}

          {/* SECTION TITLE */}
          <div className="mb-12">

            <span className="section-eyebrow">
              [ MORE READS ]
            </span>

            <h2 className="text-5xl md:text-6xl font-black text-gray-900 mt-4">
              RECENT POSTS.
            </h2>

          </div>

          {/* BLOG GRID */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">

            {rest.map((b, i) => (
              <Link
                key={b.id || i}
                to={`/blogs/${b.id || i}`}
                className="group bg-white rounded-[32px] overflow-hidden border border-[#ececef] hover:border-[#ef2435] hover:-translate-y-2 hover:shadow-2xl transition-all duration-500"
              >

                {/* IMAGE */}
                <div className="aspect-[16/10] overflow-hidden relative">

                  <img
                    src={b.image || b.img}
                    alt={b.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-60" />

                </div>

                {/* CONTENT */}
                <div className="p-7">

                  {/* TOP */}
                  <div className="flex items-center justify-between mb-5">

                    <span className="font-mono text-[10px] text-[#ef2435] tracking-[0.2em] font-semibold">
                      [ 0{i + 2} ]
                    </span>

                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <Calendar size={11} />
                      {b.published_at || b.date}
                    </div>

                  </div>

                  {/* TITLE */}
                  <h3 className="text-2xl font-bold text-gray-900 leading-tight mb-4 group-hover:text-[#ef2435] transition-colors duration-300">
                    {b.title}
                  </h3>

                  {/* EXCERPT */}
                  <p className="text-gray-600 text-sm leading-[1.9] line-clamp-3 mb-6">
                    {b.excerpt}
                  </p>

                  {/* FOOTER */}
                  <div className="pt-5 border-t border-[#ececef] flex items-center justify-between">

                    <span className="text-sm font-semibold text-[#ef2435]">
                      Read Article
                    </span>

                    <div className="w-10 h-10 rounded-2xl border border-[#ececef] flex items-center justify-center group-hover:bg-[#ef2435] group-hover:border-[#ef2435] transition-all">

                      <ArrowUpRight
                        size={16}
                        className="text-gray-700 group-hover:text-white transition-all"
                      />

                    </div>

                  </div>

                </div>

              </Link>
            ))}

          </div>

        </div>
      </section>
    </main>
  );
}